<template>
  <ion-page>
    <ion-header class="ion-no-border events-page-header liga-float-header music-library-folder-header">
      <ion-toolbar class="music-folder-float-toolbar">
        <div class="liga-float-bar music-folder-float-bar">
          <button type="button" class="liga-float-circle" aria-label="Назад" @click="goBack">
            <ion-icon :icon="chevronBackOutline" />
          </button>
          <div class="liga-float-title-pill">
            <span class="liga-float-title-text">{{ folderTitle || 'Музыка' }}</span>
          </div>
          <template v-if="showOfflineActions && !downloadProgressBar.downloading">
            <button
              v-if="!folderOffline"
              type="button"
              class="liga-float-circle"
              aria-label="Сохранить офлайн"
              @click="saveOffline"
            >
              <ion-icon :icon="cloudDownloadOutline" />
            </button>
            <template v-else>
              <span class="liga-float-circle music-folder-offline-done" aria-hidden="true">
                <ion-icon :icon="cloudDone" />
              </span>
              <button
                type="button"
                class="liga-float-circle"
                aria-label="Удалить из офлайна"
                @click="confirmRemoveFolderOffline"
              >
                <ion-icon :icon="trashOutline" />
              </button>
            </template>
          </template>
          <div v-else class="liga-float-spacer" aria-hidden="true" />
        </div>
      </ion-toolbar>
    </ion-header>
    <ion-content class="main-background-dark music-library-folder-content" :fullscreen="true">
      <div class="folder-meta-bar">
        <div class="duration-badge">
          <ion-icon :icon="timeOutline" />
          <span>{{ mediaUtils.formatTime(mediaUtils.getTotalPlaylistDuration(musicList)) }}</span>
        </div>
      </div>
      <div v-show="downloadProgressBar.downloading" class="library-download-progress">
        <div class="library-download-actions">
          <ion-button fill="outline" class="library-download-cancel" @click="stopLibraryMusicDownload">
            Отмена
          </ion-button>
          <ion-text class="library-download-meta ion-text-center">{{ downloadProgressBar.downloaded }} Мб</ion-text>
          <ion-text class="library-download-hint ion-text-center">Не закрывайте окно до завершения загрузки</ion-text>
        </div>
        <p v-if="downloadProgressBar.currentFileLabel" class="library-download-current-file ion-text-center">
          <span v-if="libraryDownloadFilePosition" class="library-download-file-index">{{ libraryDownloadFilePosition }}</span>
          {{ downloadProgressBar.currentFileLabel }}
        </p>
        <ion-progress-bar
          :value="libraryDownloadProgressValue"
          type="determinate"
          class="library-download-progress-bar"
        />
      </div>
      <div class="music-list-scroll">
        <ion-list class="bg-transparent music-library-track-list" lines="none">
          <ion-item
            v-for="(audioTrack, idx) in musicList"
            :key="audioTrack.id"
            class="music-item music-library-track-item"
            :class="{ playing: isTrackRowActive(idx) }"
            @click="selectTrack(idx)"
          >
            <div class="track-number">
              <span v-if="!isTrackRowActive(idx)">{{ idx + 1 }}</span>
              <ion-icon
                v-else
                :icon="playerBar.playing ? pause : play"
                class="playing-icon"
              />
            </div>
            <div class="track-info">
              <div class="track-name">{{ mediaUtils.getTrackName(audioTrack) }}</div>
              <div class="track-duration">{{ mediaUtils.formatTime(audioTrack.duration) }}</div>
            </div>
            <div class="track-actions">
              <template v-if="showOfflineActions">
                <ion-button
                  v-if="!audioTrack.isOffline"
                  fill="clear"
                  class="track-offline-btn"
                  :disabled="downloadProgressBar.downloading"
                  aria-label="Сохранить трек офлайн"
                  @click.stop="saveTrackOffline(audioTrack)"
                >
                  <ion-icon :icon="cloudDownloadOutline" slot="icon-only" />
                </ion-button>
                <template v-else>
                  <ion-icon :icon="cloudDone" class="offline-icon" />
                  <ion-button
                    fill="clear"
                    class="track-offline-btn track-remove-offline-btn"
                    :disabled="downloadProgressBar.downloading"
                    aria-label="Удалить трек из офлайна"
                    @click.stop="confirmRemoveTrackOffline(audioTrack)"
                  >
                    <ion-icon :icon="trashOutline" slot="icon-only" />
                  </ion-button>
                </template>
              </template>
              <ion-icon v-else-if="audioTrack.isOffline" :icon="cloudDone" class="offline-icon" />
              <div class="play-button-wrapper">
                <ion-icon
                  :icon="isTrackRowActive(idx) && playerBar.playing ? pause : play"
                  class="play-icon"
                />
              </div>
            </div>
          </ion-item>
        </ion-list>
        <p v-if="!musicList.length && !tracksLoading" class="ion-padding ion-text-center empty-tracks-hint">
          {{ emptyTracksHint }}
        </p>
        <ion-spinner v-if="tracksLoading" class="ion-margin tracks-spinner" name="crescent" />
        <div class="fake-footer" />
      </div>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import { PlayerBar } from '@/model/playerbar.model';
import { GoogleMedia } from '@/model/googlemedia.model';
import { GlobalContext } from '@/model/globalcontext.model';
import { MediaProgressBar } from '@/model/mediaprogressbar.model';
import { PlayerModes } from '@/model/player.mode';
import { Playlist } from '@/model/playlist.model';
import { libraryMusicOfflineDirName, libraryMusicService } from '@/services/library-music.service';
import MediaUtils from '@/services/media.utils';
import FilesystemCustomUtils from '@/services/filesystem.custom.utils';
import {
  alertController,
  toastController,
  IonButton,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonList,
  IonPage,
  IonProgressBar,
  IonSpinner,
  IonText,
  IonToolbar,
  isPlatform,
} from '@ionic/vue';
import { RmxAudioPlayer } from 'capacitor-plugin-playlist';
import {
  chevronBackOutline,
  cloudDone,
  cloudDownloadOutline,
  pause,
  play,
  timeOutline,
  trashOutline,
} from 'ionicons/icons';
import { defineComponent, inject } from 'vue';
// @ts-ignore
import { MittEmitter } from 'mitt-emitter';
import defaultCover from '@/theme/liga-logo-white.png';

export default defineComponent({
  name: 'MusicLibraryFolder',
  components: {
    IonPage,
    IonHeader,
    IonToolbar,
    IonContent,
    IonList,
    IonItem,
    IonIcon,
    IonButton,
    IonProgressBar,
    IonSpinner,
    IonText,
  },
  setup() {
    const playerBar = inject('playerBar') as PlayerBar;
    const audioPlayer = inject('audioPlayer') as RmxAudioPlayer;
    const emitter = inject('emitter') as MittEmitter;
    const globalContext = inject('globalContext') as GlobalContext;
    const mediaUtils = new MediaUtils();
    const filesystemCustomUtils = new FilesystemCustomUtils();
    return {
      playerBar,
      audioPlayer,
      emitter,
      globalContext,
      mediaUtils,
      filesystemCustomUtils,
      play,
      pause,
      timeOutline,
      chevronBackOutline,
      cloudDownloadOutline,
      cloudDone,
      trashOutline,
    };
  },
  data() {
    return {
      folderId: 0,
      folderTitle: '',
      folderSlug: '',
      musicList: [] as GoogleMedia[],
      isFirstSelect: true,
      coverSrc: defaultCover,
      folderOffline: false,
      downloadProgressBar: new MediaProgressBar(),
      tracksLoading: true,
      downloadListener: null as null | ((ok?: boolean) => void),
      libraryDownloadEventName: '',
    };
  },
  computed: {
    isMobile(): boolean {
      return this.globalContext.isMobile;
    },
    showOfflineActions(): boolean {
      return (
        this.isMobile &&
        !this.globalContext.offlineMode &&
        (isPlatform('ios') || isPlatform('android'))
      );
    },
    emptyTracksHint(): string {
      if (this.globalContext.offlineMode) {
        return 'Нет сохранённых треков для этой папки.';
      }
      return 'Нет треков.';
    },
    libraryDownloadProgressValue(): number {
      const p = this.downloadProgressBar;
      if (!p.downloading || p.maxValue <= 0) return 0;
      if (p.percentage > 0) return Math.min(1, p.percentage / 100);
      return Math.min(1, p.currentValue / p.maxValue);
    },
    libraryDownloadFilePosition(): string {
      const p = this.downloadProgressBar;
      if (!p.downloading || p.maxValue <= 1) return '';
      const n = Math.min(p.currentValue + 1, p.maxValue);
      return `${n} / ${p.maxValue} · `;
    },
    expectedLibraryOfflineDirectory(): string {
      return `${this.filesystemCustomUtils.PLAYLISTS_DIRECTORY}/${libraryMusicOfflineDirName(this.folderId)}`;
    },
  },
  ionViewWillEnter() {
    const id = Number(this.$route.params.folderId);
    this.folderId = id;
    this.playerBar.visible = this.playerBar.isReady && this.playerBar.musicList.length > 0;
    this.playerBar.currentMode = PlayerModes.EXTENDED;
  },
  ionViewDidEnter() {
    this.globalContext.isLoading = false;
    if (this.folderId && this.musicList.some((t) => t.isOffline)) {
      this.$nextTick(() => this.syncPlayerBarIfThisFolderOffline());
    }
  },
  mounted() {
    this.loadTracks();
    this.isFirstSelect = true;
    this.playerBar.visible = false;
    this.playerBar.isReady = false;
  },
  ionViewWillLeave() {
    this.detachDownloadListener();
    this.playerBar.fixedAlbumArtUrl = '';
  },
  methods: {
    goBack() {
      if (window.history.length > 1) {
        this.$router.back();
        return;
      }
      this.$router.push({ name: 'MusicLibrary' });
    },
    detachDownloadListener() {
      if (this.libraryDownloadEventName) {
        try {
          // mitt-emitter: off(topic) removes all handlers for this topic (unique per download id)
          this.emitter.off(this.libraryDownloadEventName);
        } catch {
          /* ignore */
        }
      }
      this.downloadListener = null;
      this.libraryDownloadEventName = '';
    },

    createUuid(): string {
      let dt = new Date().getTime();
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (dt + Math.random() * 16) % 16 | 0;
        dt = Math.floor(dt / 16);
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      });
    },

    async refreshFolderOfflineState() {
      const isNative = isPlatform('ios') || isPlatform('android');
      if (!isNative || !this.folderId || !this.musicList.length) {
        this.folderOffline = false;
        return;
      }
      const dirId = libraryMusicOfflineDirName(this.folderId);
      const updated = await Promise.all(
        this.musicList.map(async (t) => {
          const onDisk = await this.filesystemCustomUtils.isTrackOnDisk(
            this.filesystemCustomUtils.PLAYLISTS_DIRECTORY,
            dirId,
            t
          );
          return { ...t, isOffline: onDisk };
        })
      );
      this.musicList = updated;
      this.folderOffline =
        this.musicList.length > 0 && this.musicList.every((t) => t.isOffline);
    },

    async loadTracksFromDisk() {
      const dirName = libraryMusicOfflineDirName(this.folderId);
      try {
        const localPlaylist = await this.filesystemCustomUtils.loadLibraryMusicPlaylist(this.folderId);
        if (!localPlaylist?.tracks?.length) {
          this.musicList = [];
          return;
        }
        this.folderTitle = localPlaylist.name || 'Музыка';
        this.musicList = await this.filesystemCustomUtils.filterOfflineTracks(
          this.filesystemCustomUtils.PLAYLISTS_DIRECTORY,
          localPlaylist
        );
        this.folderOffline = this.musicList.length > 0;
      } catch (e) {
        console.error(e);
        this.musicList = [];
      }
    },

    isTrackRowActive(idx: number): boolean {
      return (
        !this.isFirstSelect &&
        this.playerBar.currentTrackIdx === idx &&
        !!(this.playerBar.visible || this.playerBar.microMode)
      );
    },

    async loadTracks() {
      const id = Number(this.$route.params.folderId);
      this.folderId = id;
      this.tracksLoading = true;
      if (!id) {
        this.tracksLoading = false;
        return;
      }

      const isNative = isPlatform('ios') || isPlatform('android');

      if (this.globalContext.offlineMode) {
        if (!isNative) {
          this.musicList = [];
          this.tracksLoading = false;
          return;
        }
        await this.loadTracksFromDisk();
        this.tracksLoading = false;
        return;
      }

      try {
        const tracks = await libraryMusicService.listUserTracks(id);
        this.musicList = tracks;
        const folders = await libraryMusicService.listUserFolders();
        const f = folders.find((x) => x.id === id);
        this.folderTitle = f ? f.title : 'Музыка';
        this.folderSlug = f?.slug || '';
        await this.refreshFolderOfflineState();
        this.$nextTick(() => this.syncPlayerBarIfThisFolderOffline());
      } catch (e) {
        console.error(e);
        this.musicList = [];
      } finally {
        this.tracksLoading = false;
      }
    },

    selectTrack(idx: number) {
      const expectedDir = this.expectedLibraryOfflineDirectory;
      if (
        !this.isFirstSelect &&
        this.playerBar.offlineDirectory === expectedDir &&
        this.playerBar.currentTrackIdx === idx
      ) {
        if (this.playerBar.playing) {
          this.emitter.emit('music-player-pause');
        } else {
          this.emitter.emit('music-player-play');
        }
        return;
      }

      if (this.audioPlayer && this.audioPlayer.isPlaying) {
        this.audioPlayer.pause();
      }
      this.playerBar.visible = true;
      this.playerBar.isFM = false;
      this.playerBar.currentTrackIdx = idx;
      this.playerBar.playlistName = this.folderTitle;
      this.playerBar.programShortName = '';
      this.playerBar.playlistImage = '';
      this.playerBar.playlistImageDir = '';
      this.playerBar.fixedAlbumArtUrl = this.coverSrc;
      const track = this.musicList[idx];
      this.playerBar.offline = !!track?.isOffline;
      this.playerBar.offlineDirectory = `${this.filesystemCustomUtils.PLAYLISTS_DIRECTORY}/${libraryMusicOfflineDirName(this.folderId)}`;

      if (this.isFirstSelect) {
        this.isFirstSelect = false;
        this.playerBar.musicList = this.musicList;
        this.emitter.emit('music-player-open-extended', true);
        this.emitter.emit('music-player-loaded-list', true);
      } else {
        this.emitter.emit('music-player-open-extended', true);
        this.emitter.emit('music-player-restart-and-play', true);
      }
    },

    async stopLibraryMusicDownload() {
      this.detachDownloadListener();
      this.downloadProgressBar.downloading = false;
      const pl = new Playlist(this.folderTitle, 'Музыка', 'LIB');
      pl.id = libraryMusicOfflineDirName(this.folderId);
      pl.name = this.folderTitle;
      pl.tracks = this.musicList;
      pl.isRelease = false;
      const utils = new FilesystemCustomUtils();
      await utils.cleanupPartialDownloads(pl);
      this.downloadProgressBar.currentValue = 0;
      this.downloadProgressBar.maxValue = 0;
      this.downloadProgressBar.downloaded = 0;
      this.downloadProgressBar.percentage = 0;
      this.downloadProgressBar.currentFileLabel = '';
    },

    syncPlayerBarIfThisFolderOffline() {
      if (!this.isMobile) return;
      const expectedDir = this.expectedLibraryOfflineDirectory;
      if (this.playerBar.offlineDirectory !== expectedDir) return;
      const idx = this.playerBar.currentTrackIdx;
      const track = this.musicList[idx];
      this.playerBar.offline = !!track?.isOffline;
      this.playerBar.musicList = this.musicList.slice();
      this.emitter.emit('music-player-loaded-list', this.playerBar.playing);
    },

    emitLibraryMusicOfflineChanged() {
      this.emitter.emit('library-music-offline-changed');
    },

    async confirmRemoveFolderOffline() {
      const a = await alertController.create({
        header: 'Удалить офлайн',
        message: 'Удалить сохранённые файлы этой папки с устройства? Воспроизведение снова будет из сети.',
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          { text: 'Удалить', role: 'destructive', handler: () => this.removeFolderOffline() },
        ],
      });
      await a.present();
    },

    async removeFolderOffline() {
      if (!this.folderId) return;
      const isNative = isPlatform('ios') || isPlatform('android');
      if (!isNative) return;
      await this.filesystemCustomUtils.removeLibraryMusicFolderOffline(this.folderId);
      this.folderOffline = false;
      this.musicList = this.musicList.map((t) => ({ ...t, isOffline: false }));
      const expectedDir = this.expectedLibraryOfflineDirectory;
      if (this.playerBar.offlineDirectory === expectedDir) {
        this.playerBar.offline = false;
        this.playerBar.musicList = this.musicList.slice();
        this.emitter.emit('music-player-loaded-list', this.playerBar.playing);
      }
      this.emitLibraryMusicOfflineChanged();
      const toast = await toastController.create({
        message: 'Офлайн-копия папки удалена.',
        duration: 3000,
        color: 'dark',
        position: 'top',
        cssClass: 'custom-toast',
      });
      await toast.present();
    },

    async saveOffline() {
      await this.downloadTracksOffline(this.musicList, { expectedTrackCount: this.musicList.length });
    },

    async saveTrackOffline(track: GoogleMedia) {
      await this.downloadTracksOffline([track]);
    },

    async downloadTracksOffline(
      tracks: GoogleMedia[],
      options?: { expectedTrackCount?: number }
    ) {
      if (!tracks.length) return;
      const isNative = isPlatform('ios') || isPlatform('android');
      if (!isNative) {
        const a = await alertController.create({
          header: 'Офлайн',
          message: 'Сохранение доступно в мобильном приложении.',
          buttons: ['OK'],
        });
        await a.present();
        return;
      }

      if (this.downloadProgressBar.downloading) return;

      this.detachDownloadListener();
      const progress = this.downloadProgressBar;
      progress.reset();
      progress.id = this.createUuid();
      progress.downloading = true;
      progress.startDownload();
      progress.maxValue = tracks.length;

      const utils = new FilesystemCustomUtils();
      utils.setProgressBar(progress);

      try {
        const ok = await utils.storeLibraryMusicTracks(
          this.folderId,
          this.folderTitle,
          tracks,
          {
            ...options,
            folderSlug: this.folderSlug || undefined,
          }
        );
        progress.downloading = false;
        if (!ok) {
          const a = await alertController.create({
            header: 'Ошибка',
            message: 'Не удалось сохранить музыку офлайн. Проверьте сеть и попробуйте снова.',
            buttons: ['OK'],
          });
          await a.present();
          return;
        }
        await this.refreshFolderOfflineState();
        this.syncPlayerBarIfThisFolderOffline();
        this.emitLibraryMusicOfflineChanged();
        const toast = await toastController.create({
          message: this.folderOffline
            ? 'Папка сохранена офлайн. Воспроизведение переключено на файлы устройства.'
            : 'Трек сохранён офлайн.',
          duration: 4000,
          color: 'dark',
          position: 'top',
          cssClass: 'custom-toast',
        });
        await toast.present();
      } catch (e) {
        console.error(e);
        progress.downloading = false;
        const a = await alertController.create({
          header: 'Ошибка',
          message: 'Не удалось начать загрузку.',
          buttons: ['OK'],
        });
        await a.present();
      }
    },

    async confirmRemoveTrackOffline(track: GoogleMedia) {
      const a = await alertController.create({
        header: 'Удалить из офлайна',
        message: 'Удалить сохранённый файл этого трека с устройства?',
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          {
            text: 'Удалить',
            role: 'destructive',
            handler: () => this.removeTrackOffline(track),
          },
        ],
      });
      await a.present();
    },

    async removeTrackOffline(track: GoogleMedia) {
      if (!this.folderId) return;
      const isNative = isPlatform('ios') || isPlatform('android');
      if (!isNative) return;
      await this.filesystemCustomUtils.removeLibraryMusicTrackOffline(this.folderId, track);
      await this.refreshFolderOfflineState();
      const expectedDir = this.expectedLibraryOfflineDirectory;
      if (this.playerBar.offlineDirectory === expectedDir) {
        const idx = this.playerBar.currentTrackIdx;
        const current = this.musicList[idx];
        if (current?.id === track.id) {
          this.playerBar.offline = false;
        }
        this.playerBar.musicList = this.musicList.slice();
        this.emitter.emit('music-player-loaded-list', this.playerBar.playing);
      }
      this.emitLibraryMusicOfflineChanged();
      const toast = await toastController.create({
        message: 'Трек удалён из офлайна.',
        duration: 3000,
        color: 'dark',
        position: 'top',
        cssClass: 'custom-toast',
      });
      await toast.present();
    },
  },
});
</script>

<style scoped>
.music-folder-float-toolbar {
  --background: transparent;
  --padding-start: 0;
  --padding-end: 0;
  --padding-top: env(safe-area-inset-top);
  --border-width: 0;
  --min-height: 56px;
}

.music-folder-float-bar .liga-float-circle {
  appearance: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: var(--liga-chrome-text);
  flex-shrink: 0;
}

.music-folder-float-bar .liga-float-circle ion-icon {
  font-size: 22px;
  color: var(--liga-chrome-text);
}

.music-folder-offline-done {
  cursor: default;
  pointer-events: none;
  color: var(--liga-chrome-text);
}

.music-folder-float-bar .liga-float-title-pill {
  min-width: 0;
}

.library-download-progress {
  padding: 8px 16px 12px;
  width: 100%;
  box-sizing: border-box;
}
.library-download-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  margin-bottom: 8px;
}
.library-download-cancel {
  --border-color: var(--liga-float-glass-border);
  --color: var(--liga-chrome-text);
  flex-shrink: 0;
}
.library-download-meta,
.library-download-hint {
  color: var(--liga-chrome-text);
  font-size: 0.8rem;
  flex: 1 1 120px;
}
.library-download-hint {
  color: var(--liga-chrome-text-muted);
  flex: 1 1 200px;
}
.library-download-current-file {
  margin: 0 8px 10px;
  font-size: 0.78rem;
  line-height: 1.35;
  color: var(--liga-chrome-text);
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.library-download-file-index {
  color: var(--liga-chrome-text-muted);
  font-weight: 500;
}
.library-download-progress-bar {
  height: 5px;
  --progress-background: var(--ion-color-orange);
  --background: var(--liga-surface);
  border-radius: 4px;
}

.music-library-folder-content {
  --color: var(--liga-chrome-text);
  color: var(--liga-chrome-text);
}

.folder-meta-bar {
  display: flex;
  justify-content: center;
  padding: 8px 16px 4px;
  width: 100%;
  box-sizing: border-box;
}
.duration-badge {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.35rem 0.9rem;
  border-radius: 999px;
  color: var(--liga-chrome-text);
  font-size: 0.82rem;
  font-weight: 550;
  background: var(--liga-float-glass-bg);
  border: 1px solid var(--liga-float-glass-border);
  backdrop-filter: var(--liga-float-glass-blur);
  -webkit-backdrop-filter: var(--liga-float-glass-blur);
  box-shadow: var(--liga-float-glass-shadow);
}
.music-library-track-list {
  padding: 4px 12px 8px;
  background: transparent !important;
  --background: transparent !important;
}

.music-list-scroll {
  max-height: calc(100vh - 180px);
  overflow-y: auto;
}
.fake-footer {
  height: 20vh;
}
.music-item {
  --background: var(--liga-float-glass-bg);
  --background-hover: var(--liga-float-glass-bg-strong);
  --background-activated: var(--liga-float-glass-bg-strong);
  --padding-start: 16px;
  --padding-end: 16px;
  --min-height: 60px;
  --color: var(--liga-chrome-text);
  margin-bottom: 10px;
  border-radius: 14px;
  border: 1px solid var(--liga-float-glass-border);
  box-shadow: var(--liga-float-glass-shadow);
  backdrop-filter: var(--liga-float-glass-blur);
  -webkit-backdrop-filter: var(--liga-float-glass-blur);
}

.music-item::part(native) {
  border-radius: 14px;
}

.track-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.offline-icon {
  color: var(--ion-color-success);
  font-size: 1.15rem;
}

.track-offline-btn {
  --color: var(--liga-chrome-text);
  --padding-start: 4px;
  --padding-end: 4px;
  margin: 0;
  height: 32px;
}

.track-remove-offline-btn {
  --color: var(--liga-chrome-text-muted);
}

.playing-icon {
  color: var(--ion-color-orange) !important;
  font-size: 1.15rem;
}

.track-number {
  width: 28px;
  text-align: center;
  color: var(--liga-chrome-text-muted) !important;
}
.track-info {
  flex: 1;
  margin-left: 12px;
}
.track-name {
  color: var(--liga-chrome-text) !important;
  font-weight: 500;
}
.track-duration {
  font-size: 0.8rem;
  color: var(--liga-chrome-text-muted) !important;
}
.play-icon {
  font-size: 1.4rem;
  color: var(--ion-color-orange) !important;
}
.play-button-wrapper {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(235, 89, 82, 0.18);
  border-radius: 50%;
}
.playing .play-button-wrapper {
  background: rgba(235, 89, 82, 0.28);
}
.playing .track-name {
  color: var(--ion-color-orange) !important;
}

.empty-tracks-hint {
  color: var(--liga-chrome-text-muted);
  font-size: 0.95rem;
}

.tracks-spinner {
  display: block;
  margin: 16px auto;
  color: var(--ion-color-orange);
}
</style>
